import { toast } from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { PlusIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import { captureException } from '@sentry/react';

import { useAllGeneralSettings } from '../../all-settings-context';
import { EmailType, Language } from 'src/types/enums';
import { linkEmailAttachment, unlinkEmailAttachment, updateEmailContent, useEmailParams } from '../../models';
import { getDisplayError } from '../../../../utils/get-display-error';
import { nullthrows } from '../../../../utils/invariant';
import { MultiFileUploaderButton } from '../../../document/components/MultiFileUploaderButton';
import { EmailNames } from './constants';
import { Button } from '../../../../components/button/Button';
import { TemplateTextAreaField } from '../../../../components/textarea/TemplateTextAreaField';
import { Card } from '../../../../components/Card';

const EmailTypeSettingsPage = () => {
  const { emailType: _emailType } = useParams<{ emailType: string }>();
  const emailType = nullthrows(_emailType, 'Email type is a required route parameter') as EmailType;
  if (!Object.values(EmailType).includes(emailType as EmailType)) {
    throw new Error(`Invalid email type: ${emailType}`);
  }

  const { settings } = useAllGeneralSettings();
  const { data: _emailParams } = useEmailParams(emailType);

  const emailParams = _emailParams ?? [];
  return (
    <div className="grid 2xl:grid-cols-2 gap-4">
      {[Language.Nl, Language.En, Language.Fr, Language.De].map((language) => {
        const subject =
          settings.emailContents.find((c) => c.language === language && c.emailType === emailType)?.subject ?? '';
        const content =
          settings.emailContents.find((c) => c.language === language && c.emailType === emailType)?.content ?? '';

        return (
          <Card
            key={`${emailType}-${language}`}
            title={`${EmailNames[emailType]} - ${language}`}
            rightHeading={
              <MultiFileUploaderButton
                buttonText="Bijlagen"
                title={`Bijlagen ${EmailNames[emailType]} - ${language}`}
                files={settings.emailAttachments
                  .filter((a) => a.emailType === emailType && a.language === language)
                  .map((a) => a.document)}
                onSubmit={async (documents) => {
                  let count = 0;
                  await Promise.allSettled(
                    documents.map(async (doc) => {
                      try {
                        await linkEmailAttachment({
                          emailType,
                          documentId: doc.id,
                          language,
                        });
                        count += 1;
                        toast.success(`Bestand: ${doc.name}, ${count}/${documents.length} opgeladen`);
                      } catch (err) {
                        captureException(err);
                        toast.error(`Kon bestand ${doc.name} niet opladen: ${getDisplayError(err)}`);
                      }
                    }),
                  );
                }}
                unlinkDocument={async (docId: string) => {
                  try {
                    const attachment = nullthrows(
                      settings.emailAttachments.find((a) => a.document.id === docId),
                      'Attachment not found',
                    );
                    await unlinkEmailAttachment(attachment.id);
                    toast.success('Bestand gewist');
                  } catch (err) {
                    captureException(err);
                    toast.error(`Kon bestand niet wissen: ${getDisplayError(err)}`);
                  }
                }}
              />
            }
          >
            <div>
              <div className="section-header mb-2">Inhoud</div>
              <Formik
                initialValues={{ subject, content }}
                onSubmit={async (values) => {
                  try {
                    const { subject, content } = values;
                    await updateEmailContent({
                      emailType,
                      language,
                      subject,
                      content,
                    });
                    toast.success('Email inhoud aangepast');
                  } catch (err: any) {
                    captureException(err);
                    toast.error('Kon email inhoud niet aanpassen: ' + getDisplayError(err));
                  }
                }}
              >
                {({ handleSubmit, isSubmitting }) => (
                  <form onSubmit={handleSubmit}>
                    <TemplateTextAreaField labelText="Onderwerp" name="subject" params={emailParams} rows={1} />
                    <TemplateTextAreaField labelText="Inhoud" name="content" params={emailParams} rows={10} />

                    <Button
                      type="submit"
                      color="primary"
                      isDisabled={isSubmitting}
                      isLoading={isSubmitting}
                      iconLeft={<PlusIcon className="button-icon" />}
                    >
                      Pas email inhoud aan
                    </Button>
                  </form>
                )}
              </Formik>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default EmailTypeSettingsPage;
